<script lang="ts">
    import { Component, Prop, Vue, Watch } from 'vue-facing-decorator';
    import Spinner from '../../helpers/Spinner.vue';
    import ContextMenu from '../../helpers/ContextMenu.vue';
    import ContextMenuButton from '../../helpers/ContextMenu/ContextMenuButton.vue';
    import * as api from '../../../api/ApiClient';
    import * as apiBase from '../../../api/ApiClientBase';
    import * as ext from '../../../api/Extensions';
    import RouteNames from '../../../routing/RouteNames';
    import Confirm from '../../helpers/Confirm.vue';
    import { Localizer } from '../../../api/Localizer';
    import XLCubedHelper from '../../../api/XLCubedHelper';
    import { ChatWindowsTabData, WindowsTabData, FsoWindowsTabData } from '../../../api/WindowsTabData';

    import { ChatUser, ChatMessage } from '../chat/ChatUser';
    import {IAlmFileSystemObjectContent} from "@/components/pages/landscape/LandscapeManagementInterfaces";
    import ModuleAuthHelper from '@/api/ModuleAuthHelper';
    import FileSystemObjectIcon from '@/components/helpers/FileSystemObjectIcon.vue';
    import CommonButton from "@/components/helpers/CommonButton.vue";
    import { ButtonType } from "@/components/helpers/HelpersInterface";

    @Component({ name: 'FileSystemObject', components: { Spinner, ContextMenu, ContextMenuButton, Confirm, FileSystemObjectIcon, CommonButton } })
    export default class FileSystemObject extends Vue {
        @Prop() public isAlm: boolean | undefined;
        @Prop() private Fso!: api.FileSystemObjectContent;
        @Prop() private CanUserExpand!: boolean;
        @Prop() private HideExpander!: boolean;
        @Prop() private CanUserSelect!: boolean;
        @Prop() private CanUserOpenWithDoubleClick!: boolean;
        @Prop() private ChangeUrlOnSelect: boolean | undefined;
        @Prop() private IsParentFolderDisabled!: boolean;
        @Prop() private Secondary!: boolean;
        @Prop() private HighlightSearchResults!: boolean;

        private Localizer = new Localizer(this.$store);
        private FileSystemType = api.FileSystemType;
        ButtonType = ButtonType;


        get Working(): boolean {
            return this.$store.state.explorer.Working;
        }
        set Working(val: boolean) {
            this.$store.commit('explorer/SetWorking', val);
        }

        private IsExpanded: boolean = false;
        private get fsoExpandedState(): boolean[] {
            return this.Secondary ? this.$store.state.explorer.SecondaryFsoExpandedState :
                this.$store.state.explorer.FsoExpandedState;
        }
        mounted() {
            if (this.Fso.objectType == this.FileSystemType.Folder && this.IsSelected) {
                const el = this.$refs.fso as HTMLDivElement;
                el.scrollIntoView({ block: 'nearest' });
            }

            const testId = this.$route.query['openFso'];
            if (testId) {
                const idString = testId as string;
                if (idString === this.Fso.id.toString() || (idString == this.FullPath)) {
                    this.Fso_Dblclick();
                }
            }
        }

        async Refresh() {
            try {
                this.Working = true;
                await this.$store.dispatch('explorer/LoadFileSystem');
            }
            finally {
                this.Working = false;
                this.CloseContextMenu();
            }
        }

        get FullPath(): string {
            let fullPath = `${this.Fso.path}/${this.Fso.name}`;
            fullPath = fullPath.replace('//', '/').replace('//', '/');
            return fullPath;
        }

        get almBadgeCount() {
            return (fso: IAlmFileSystemObjectContent):number => {
                let count = 0;
                const getAlmItemsChecked = (almFso: IAlmFileSystemObjectContent) => {
                    almFso.folderContent?.forEach((item:IAlmFileSystemObjectContent) => {
                        if (item.almChecked || item.almIntermediateChecked) {
                            count += 1;
                        }
                    })
                }
                getAlmItemsChecked(fso);
                return count;
            }
        }

        get IsGlobal(): boolean {
            return this.Fso.id < 0;
        }

        get IsFavorite(): boolean {
            const favs = this.$store.state.explorer.Favorites as string[] | null;
            const path = this.Fso.fullPath;

            if (favs != null) {
                for (let i = 0; i < favs.length; i++) {
                    const fav = favs[i];
                    if (fav == path) {
                        return true;
                    }
                }
            }
            return false;
        }

        private async ToggleFavorite() {
            await this.$store.dispatch('explorer/ToggleFavorite', this.Fso.fullPath);
        }

        get ModuleSecurityAccess(): api.Module[] | null {
            return this.$store.state.security.ModuleSecurity;
        }

        get FolderRights(): api.AccessRights {
            return this.Fso.folderRights;
        }
        get ObjectRights(): api.AccessRights {
            return this.Fso.objectRights;
        }

        get IsThisFolderDisabled(): boolean {
            return this.Fso.objectType == api.FileSystemType.Folder && this.FolderRights == api.AccessRights.None;
        }

        get FolderChildren(): Array<api.FileSystemObjectContent> {
            if (!this.IsThisFolderDisabled && this.Fso.folderContent != null) {
                return this.Fso.folderContent
                    .filter(c => c.objectType == api.FileSystemType.Folder)
                    .sort((a, b) => a.name!.localeCompare(b.name!));
            }
            else {
                return new Array<api.FileSystemObjectContent>();
            }
        };

        get CanShowContextMenu(): boolean {
            return !this.IsThisFolderDisabled && !this.Secondary;
        }

        get UserCanView(): boolean {
            return !this.IsParentFolderDisabled &&
                (this.Fso.objectType == api.FileSystemType.Folder || this.FolderRights > api.AccessRights.None);
        }

        get HasChildren(): boolean {
            return !this.IsThisFolderDisabled && this.CanUserExpand && this.FolderChildren.length > 0;
        };

        get IsSelected(): boolean {
            const selectedFso =
                (this.Secondary ?
                    this.$store.state.explorer.SecondarySelectedFso :
                    this.$store.state.explorer.SelectedFso) as api.FileSystemObjectContent | null;
            if (selectedFso != null && this.Fso.fullPath != null &&
                this.Fso.objectType == api.FileSystemType.Folder &&
                this.CanUserExpand && this.Fso.folderContent != null) {
                if (selectedFso.fullPath == this.Fso!.fullPath ||
                    selectedFso.fullPath?.startsWith(`${this.Fso!.fullPath}/`)) {
                    this.IsExpanded = true;
                    this.fsoExpandedState[this.Fso.id] = true;
                }
            }
            return this.Fso === selectedFso;
        }

        get IsCutCopySelected(): boolean {
            const po = this.$store.state.explorer.PasteOperationDetails as ext.PasteOperation;
            return po != null && po.Fso === this.Fso;
        }

        Expander_Click() {
            this.IsExpanded = !this.IsExpanded;
            this.fsoExpandedState[this.Fso.id] = this.IsExpanded;

        };

        async Fso_Click() {
            if (this.CanUserSelect && !this.IsSelected && !this.IsThisFolderDisabled) {
                if (this.Secondary) {
                    this.$store.commit('explorer/SelectSecondaryFso', this.Fso);
                }
                else {
                    this.$store.commit('explorer/SelectFso', this.Fso);
                }

                if (this.ChangeUrlOnSelect == undefined || this.ChangeUrlOnSelect == true) {
                    this.$router.push(
                        {
                            name: RouteNames.ExplorerRoute,
                            query: { selectedFso: this.Fso.id.toString() }
                        });
                }
            }
            if (this.HideExpander && this.Fso.objectType == api.FileSystemType.Folder) {
                if (this.Secondary) {
                    this.$store.commit('explorer/SelectSecondaryFso', this.Fso);
                }
                else {
                    this.$store.commit('explorer/SelectFso', this.Fso);
                }

                await this.$store.dispatch('explorer/ExpandExplorerFolders',
                    {
                        targetFso: this,
                        secondary: this.Secondary
                    });

                if (this.ChangeUrlOnSelect == undefined || this.ChangeUrlOnSelect == true) {
                    this.$router.push({
                        name: RouteNames.ExplorerRoute,
                        query:
                            { selectedFso: this.Fso.id.toString() }
                    });
                }
            }
        };

        Fso_Dblclick() {
            if (this.CanUserOpenWithDoubleClick) {
                if (this.Fso.objectType == this.FileSystemType.ReportBook) {
                    return this.Fso_Run();
                }
                if (this.Fso.objectType == this.FileSystemType.Folder &&
                    this.CanUserExpand && !this.IsThisFolderDisabled) {
                    this.IsExpanded = !this.IsExpanded;
                    this.fsoExpandedState[this.Fso.id] = this.IsExpanded;
                }
                else if (this.Can_Open) {
                    return this.Fso_Open();
                }
            }
        }

        ContextMenuEvent: MouseEvent | null = null;
        get ShowContextMenu(): boolean {
            return this.ContextMenuEvent != null;
        }
        Fso_ContextMenu(event: MouseEvent) {
            if (this.isAlm) {
                return false;
            }
            this.$store.commit('explorer/SetPasteTargetFso', this.Fso);
            this.ContextMenuEvent = event;
        }
        CloseContextMenu() {
            this.ContextMenuEvent = null;
        }

        get UserCanViewWorkflow(): boolean {
            return true;
        }

        get UserCanEditWorkflow(): boolean {
            const ModuleSecurityAccess = this.$store.state.security.ModuleSecurity;
            return ModuleSecurityAccess != null &&
                ModuleAuthHelper.ModuleIdInAccess(ModuleSecurityAccess,api.ModuleAccess.WorkflowManagementManage);
        }

        get UserCanRunWorkflow(): boolean {
            const ModuleSecurityAccess = this.$store.state.security.ModuleSecurity;
            return ModuleSecurityAccess != null &&
                ModuleAuthHelper.ModuleIdInAccess(ModuleSecurityAccess,api.ModuleAccess.WorkflowManagementControl);
        }

        get UserCanViewForm(): boolean {
            const ModuleSecurityAccess = this.$store.state.security.ModuleSecurity;
            return ModuleSecurityAccess != null && (ModuleAuthHelper.ModuleIdInAccess(ModuleSecurityAccess,api.ModuleAccess.BasicForms)
                || ModuleAuthHelper.ModuleIdInAccess(ModuleSecurityAccess,api.ModuleAccess.AdvancedForms));
        }

        private get Can_Open(): boolean {
            switch (this.Fso.objectType) {
                case this.FileSystemType.Process:
                case this.FileSystemType.InteractiveProcess:
                    return this.ObjectRights >= api.AccessRights.Read && this.UserCanViewWorkflow;
                case this.FileSystemType.Form:
                    return this.ObjectRights >= api.AccessRights.Read && this.UserCanViewForm;
                default:
                    return this.ObjectRights >= api.AccessRights.Read;
            }
            return false;
        }

        private async Fso_Open() {
            if (this.Can_Open) {
                switch (this.Fso.objectType) {
                    case this.FileSystemType.Generic:
                        if (this.Fso.path) {
                            this.Working = true;
                            try {
                                const authClient = apiBase.AuthClient.getInstance();
                                await authClient.ensureToken();
                                const fsClient = new api.FileSystemClient(authClient);

                                const file = await fsClient.getFile(this.FullPath, 'all', false);
                                const content = file.binaryContent!;
                                const byteArray = content.toUint8Array();
                                const blob = new Blob([byteArray], { type: file.textContent! });
                                const link = document.createElement('a');
                                link.href = URL.createObjectURL(blob);
                                link.download = file.name!;
                                link.click();
                                URL.revokeObjectURL(link.href);
                            }
                            catch (err) {
                                this.$store.dispatch('ShowToastError', err);
                            }
                            this.Working = false;
                        }
                        break;

                    case this.FileSystemType.Process:
                    case this.FileSystemType.InteractiveProcess:
                        this.Working = true;
                        if (this.UserCanViewWorkflow) {
                            try {
                                await this.$store.dispatch('workflow/LoadWorkflow', this.Fso);
                                this.$router.push({
                                    name: RouteNames.WorkflowRoute,
                                    query: { id: this.Fso.id.toString() }
                                });
                            }
                            catch (err) {
                                this.$store.dispatch('ShowToastError', err);
                            }
                        }
                        this.Working = false;
                        break;

                    case this.FileSystemType.Form:
                        if (this.Fso.path && this.UserCanViewForm) {
                            this.Working = true;
                            try {
                                const authClient = apiBase.AuthClient.getInstance();
                                await authClient.ensureToken();
                                const client = new api.FormClient(authClient);

                                const response = await client.generateOpenControlTab(this.FullPath);
                                if (response) {
                                    const blob = new Blob([response.data],
                                        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                                    const link = document.createElement('a');
                                    link.href = URL.createObjectURL(blob);
                                    // response.fileName is undefined if x-api-token auth is not used -
                                    // for some reason the header content - disposition(which is gives
                                    // fileName in ApiClient.ts) is missing
                                    // otherwise, the file name 'Open-{file id}.xlsx' should come from
                                    // FormController.GenerateOpenControlTab()
                                    link.download = response.fileName ? response.fileName : `Open-${this.Fso.id}.xlsx`;
                                    link.click();
                                    URL.revokeObjectURL(link.href);
                                }
                            }
                            catch (err) {
                                this.$store.dispatch('ShowToastError', err);
                            }
                            this.Working = false;
                        }
                        break;

                    case this.FileSystemType.PowerBI:
                        const id = this.Fso.id;
                        this.Working = false;
                        this.$router.push(`report/${id.toString()}`);
                        break;

                    case this.FileSystemType.XL3:
                        const id2 = this.Fso.id;
                        this.Working = false;
                        this.$router.push(`xl3/${id2.toString()}`);
                        break;

                    case this.FileSystemType.DocxReport:
                    case this.FileSystemType.PptxReport:
                        this.$emit('OpenOfficeReport');
                        break;

                    default:
                        break;
                }

                this.CloseContextMenu();
            }
        }

        get Tenant(): api.Tenant {
            return this.$store.state.security.Tenant;
        }

        private get Can_OpenInExcel(): boolean {
            return this.ObjectRights >= api.AccessRights.Read && this.Fso.objectType == api.FileSystemType.XL3;
        }
        private async Fso_OpenInExcel() {
            if (this.Can_OpenInExcel) {

                const xl3Helper = new XLCubedHelper(this.$store, this.$route);

                this.CloseContextMenu();
                this.Working = true;
                try {
                    await xl3Helper.getExcelFile(this.Fso.id.toString(), this.Tenant.name!, this.Fso.name!);
                }
                catch (err) {
                    this.$store.dispatch('ShowToastError', err);
                }
                finally {
                    this.Working = false;
                }

            }
        }

        private get Can_OpenInPopup(): boolean {
            return this.ObjectRights >= api.AccessRights.Read;
        }

        private async Fso_OpenInPopup() {
            const tabs = this.$store.state.windowstabs.Tabs as WindowsTabData[];
            const name = this.Fso.name ?? '';
            let testName = '';
            const idx = 0;
            for (let idx = 0; idx < 100; idx++) {
                let ok = true;
                testName = name + (idx == 0 ? '' : (` ${idx.toString()}`));
                for (let i = 0; i < tabs.length; i++) {
                    if (tabs[i].Name == testName) {
                        ok = false;
                    }
                }
                if (ok) {
                    tabs.push(new FsoWindowsTabData(testName, this.Fso));
                    break;
                }
            }
        }

        private get Can_OpenInTab(): boolean {
            return this.Can_Open;
        }

        private async Fso_OpenNewTab() {
            if (this.Can_Open) {

                const xl3Helper = new XLCubedHelper(this.$store, this.$route);
                await xl3Helper.openNewTab(this.Fso.id.toString(), this.Tenant.name!);

                this.CloseContextMenu();
            }
        }

        private get Can_SaveAs(): boolean {
            return this.ObjectRights >= api.AccessRights.Read && this.Fso.objectType == api.FileSystemType.XL3;
        }
        private async Fso_SaveAs(type: string) {
            if (this.Can_SaveAs) {
                const auth_client = apiBase.AuthClient.getInstance();
                await auth_client.ensureToken();
                let format = api.ArchiveXL3ReportFormat.Pdf;
                let extension = '.pdf';
                switch (type) {
                    case 'pdf':
                        format = api.ArchiveXL3ReportFormat.Pdf;
                        extension = '.pdf';
                        break;
                    case 'excel':
                        format = api.ArchiveXL3ReportFormat.Excel;
                        extension = '.xlsx';
                        break;
                    case 'powerpoint':
                        format = api.ArchiveXL3ReportFormat.Powerpoint;
                        extension = '.pptx';
                        break;
                }
                this.Working = true;
                try {
                    const client1 = new api.FileSystemClient(auth_client);
                    const response = await client1.renderXL3Report(this.Fso.fullPath!, format);
                    if (response) {
                        const blob = new Blob([response.data]);
                        const link = document.createElement('a');
                        link.href = URL.createObjectURL(blob);
                        link.download = response.fileName ?? this.Fso.name + extension;
                        link.click();
                        URL.revokeObjectURL(link.href);
                    }
                }
                catch (err) {
                    this.$store.dispatch('ShowToastError', err);
                }
                finally {
                    this.CloseContextMenu();
                    this.Working = false;
                }
            }
        }

        private get Can_AccessObjectContents(): boolean {
            return this.ObjectRights > api.AccessRights.None;
        }
        private get Can_Run(): boolean {
            return this.Can_AccessObjectContents;
        }
        private async Fso_Run() {
            if (this.Can_Run) {
                this.Working = true;
                if (this.Fso.objectType == api.FileSystemType.CalcScript) {
                    try {
                        const authClient = apiBase.AuthClient.getInstance();
                        await authClient.ensureToken();
                        const client = new api.QueryClient(authClient);

                        const id = this.Fso.id.toString();
                        await client.runCalcFile(id, [], true);
                        await this.$store.dispatch('ShowToast', new api.ToastNotification({
                            message: this.Localizer.Localize('FileSystemObject_Calc_Started'),
                            type: api.ToastType.Info
                        }));
                    }
                    catch (err) {
                        this.$store.dispatch('ShowToastError', err);
                        return;
                    }
                    finally {
                        this.Working = false;
                    }
                }
                else if (this.Fso.objectType == api.FileSystemType.ReportBook) {

                    let reportBook: api.ReportBookItem;
                    try {
                        const authClient = apiBase.AuthClient.getInstance();
                        await authClient.ensureToken();
                        const client = new api.ReportBookClient(authClient);

                        const id = this.Fso.id.toString();
                        reportBook = await client.load(id);
                    }
                    catch (err) {
                        this.$store.dispatch('ShowToastError', err);
                        return;
                    }
                    finally {
                        this.Working = false;
                    }

                    // create new object with extension methods
                    const parsed = new ext.ReportBookItem(reportBook);
                    if (parsed.parameters && parsed.parameters.length > 0) {
                        this.$store.commit('reportbook/SetReportBook', parsed);
                        this.$emit('RunReportBook');
                    }
                    else {
                        this.$store.dispatch('reportbook/RunReportBook', parsed);
                    }
                }

                this.CloseContextMenu();
            }
        }

        private get Can_EditFolderContents(): boolean {
            if (this.IsGlobal && !this.$store.state.security.UserData.isSuperUser) {
                return false;
            }
            return this.FolderRights == api.AccessRights.Write;
        }

        private get Can_Edit(): boolean {
            switch (this.Fso.objectType) {
                case api.FileSystemType.Form:
                case api.FileSystemType.ReportBook:
                case api.FileSystemType.DocxReport:
                case api.FileSystemType.PptxReport:
                    return this.Can_EditFolderContents && this.Can_AccessObjectContents;
                default:
                    return this.Can_EditFolderContents;
            }
        }
        private async Fso_Edit() {
            if (this.Can_Edit) {
                try {
                    this.Working = true;

                    if (this.Fso.objectType == api.FileSystemType.Form) {
                        if (this.Fso.path) {
                            const authClient = apiBase.AuthClient.getInstance();
                            await authClient.ensureToken();
                            const client = new api.FormClient(authClient);

                            const response = await client.generateEditControlTab(this.FullPath);
                            if (response) {
                                const blob = new Blob([response.data],
                                    { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                                const link = document.createElement('a');
                                link.href = URL.createObjectURL(blob);
                                // response.fileName is undefined if x-api-token auth is not used -
                                // for some reason the header content -
                                // disposition(which defines fileName in ApiClient.ts) is missing
                                // otherwise, the file name 'Edit-{file id}.xlsx'
                                //should come from FormController.GenerateEditControlTab()
                                link.download = response.fileName ? response.fileName : `Edit-${this.Fso.id}.xlsx`;
                                link.click();
                                URL.revokeObjectURL(link.href);
                            }
                        }
                    }
                    else if (this.Fso.objectType == api.FileSystemType.ReportBook) {
                        await this.$store.dispatch('reportbook/LoadReportBook', this.Fso);
                        this.$router.push({ name: RouteNames.ReportBookRoute, query: { id: this.Fso.id.toString() } });
                    }
                    else if (this.Fso.objectType == api.FileSystemType.DocxReport ||
                        this.Fso.objectType == api.FileSystemType.PptxReport) {
                        const auth_client = apiBase.AuthClient.getInstance();
                        await auth_client.ensureToken();
                        const client = new api.OfficeClient(auth_client);
                        if (this.Fso.path) {
                            let fullPath = `${this.Fso.path}/${this.Fso.name}`;
                            fullPath = fullPath.replace('//', '/').replace('//', '/');

                            const file = await client.getReport(fullPath);
                            this.$store.commit('explorer/SetOfficeReportToEdit', file);
                        }
                    }
                    this.CloseContextMenu();
                }
                catch (err) {
                    this.$store.dispatch('ShowToastError', err);
                }
                this.Working = false;
            }
        }

        private get Can_NewFolderObjects(): boolean {
            return this.Can_EditFolderContents;
        }

        private get Can_NewFolder(): boolean {
            return this.Can_NewFolderObjects;
        }
        private async Fso_NewFolder() {
            if (this.Can_NewFolder) {
                this.Fso_Click();
                this.$store.commit('explorer/SetNewFsoType', api.FileSystemType.Folder);
                this.CloseContextMenu();
            }
        }

        private get UserHasWorkflowModule(): boolean {
            return this.ModuleSecurityAccess != null &&
                ModuleAuthHelper.ModuleIdInAccess(this.ModuleSecurityAccess,api.ModuleAccess.WorkflowManagementManage);
        }
        private get Can_NewProcess(): boolean {
            return this.Can_NewFolderObjects && this.UserHasWorkflowModule;
        }
        private async Fso_NewProcess() {
            if (this.Can_NewProcess) {
                this.Fso_Click();
                this.$store.commit('explorer/SetNewFsoType', api.FileSystemType.InteractiveProcess);
                this.CloseContextMenu();
            }
        }

        private get Can_NewReportBook() {
            return this.Can_NewFolderObjects;
        }
        private async Fso_NewReportBook() {
            if (this.Can_NewReportBook) {
                this.Fso_Click();
                this.$store.commit('explorer/SetNewFsoType', api.FileSystemType.ReportBook);
                this.CloseContextMenu();
            }
        }

        private get Can_NewPowerBI(): boolean {
            return this.Can_NewFolderObjects;
        }
        private async Fso_NewPowerBI() {
            if (this.Can_NewPowerBI) {
                this.Fso_Click();
                this.$store.commit('explorer/SetNewFsoType', api.FileSystemType.PowerBI);
                this.CloseContextMenu();
            }
        }

        private get Can_NewXL3(): boolean {
            return this.Can_NewFolderObjects;
        }
        private async Fso_NewXL3() {
            if (this.Can_NewXL3) {
                this.Fso_Click();
                this.$store.commit('explorer/SetNewFsoType', api.FileSystemType.XL3);
                this.CloseContextMenu();
            }
        }

        private get Can_NewOfficeReport(): boolean {
            return this.Can_NewFolderObjects;
        }
        private async Fso_NewDocxReport() {
            if (this.Can_NewOfficeReport) {
                this.Fso_Click();
                this.$store.commit('explorer/SetNewFsoType', api.FileSystemType.DocxReport);
                this.CloseContextMenu();
            }
        }
        private async Fso_NewPptxReport() {
            if (this.Can_NewOfficeReport) {
                this.Fso_Click();
                this.$store.commit('explorer/SetNewFsoType', api.FileSystemType.PptxReport);
                this.CloseContextMenu();
            }
        }

        private get Can_NewGeneric(): boolean {
            return this.Can_NewFolderObjects;
        }
        private async Fso_NewGeneric() {
            if (this.Can_NewGeneric) {
                this.Fso_Click();
                this.$store.commit('explorer/SetNewFsoType', api.FileSystemType.Generic);
                this.CloseContextMenu();
            }
        }

        private get Can_NewCalcScript(): boolean {
            return this.Can_NewFolderObjects;
        }
        private async Fso_NewCalcScript() {
            if (this.Can_NewCalcScript) {
                this.Fso_Click();
                this.$store.commit('explorer/SetNewFsoType', api.FileSystemType.CalcScript);
                this.CloseContextMenu();
            }
        }

        ShowConfirmDelete: boolean = false;
        OpenConfirmDelete(item: api.DashboardControl) {
            this.ShowConfirmDelete = true;
        }
        CloseConfirmDelete() {
            this.ShowConfirmDelete = false;
        }

        private get Can_Delete(): boolean {
            if (this.Fso.name == '/')
                return false;
            if (this.IsGlobal && !this.$store.state.security.UserData.isSuperUser)
                return false;
            if (this.Fso.objectType == api.FileSystemType.Folder)
                return this.FolderRights == api.AccessRights.Write;
            return this.ObjectRights >= api.AccessRights.Write;
        }
        private async Fso_Delete() {
            if (this.Can_Delete) {
                this.Working = true;
                try {
                    const temp = this.$store.state.explorer.SelectedFso;
                    this.$store.commit('explorer/SelectFso', this.Fso);
                    await this.$store.dispatch('explorer/DeleteSelectedItem');
                    this.$store.commit('explorer/SelectFso', temp);

                    this.CloseContextMenu();
                }
                catch (err) {
                    this.$store.dispatch('ShowToastError', err);
                }
                this.Working = false;
            }
        }

        private get Can_Rename(): boolean {
            const result = this.Can_EditFolderContents && this.Fso.name != '/';
            return result;
        }
        private async Fso_Rename() {
            if (this.Can_Rename) {
                this.$store.commit('explorer/SetFsoToRename', this.Fso);
                this.CloseContextMenu();
            }
        }

        private get Can_Copy(): boolean {
            const result = this.Can_AccessObjectContents && this.Can_EditFolderContents && this.Fso.name != '/';
            return result;
        }

        private Fso_Copy() {
            if (this.Can_Copy) {
                const payload = new ext.PasteOperation();
                payload.Fso = this.Fso;
                payload.Type = ext.PasteOperationType.copy;

                this.$store.commit('explorer/SetPasteOperationDetails', payload);

                this.CloseContextMenu();
            }
        }

        private get Can_Cut(): boolean {
            const result = this.Can_AccessObjectContents && this.Can_EditFolderContents && this.Fso.name != '/';
            return result;
        }
        private Fso_Cut() {
            if (this.Can_Cut) {
                const payload = new ext.PasteOperation();
                payload.Fso = this.Fso;
                payload.Type = ext.PasteOperationType.cut;

                this.$store.commit('explorer/SetPasteOperationDetails', payload);

                this.CloseContextMenu();
            }
        }

        private get Can_Paste(): boolean {
            if (!this.Can_NewFolderObjects) {
                return false;
            }
            else {
                const pasteOperation = this.$store.state.explorer.PasteOperationDetails as ext.PasteOperation;
                if (pasteOperation.Type == ext.PasteOperationType.none || pasteOperation.Fso == null) {
                    return false;
                }

                const hierarchy = this.$store
                    .getters['explorer/getFsoHierarchyById'](this.Fso.id) as api.FileSystemObjectContent[] | null;
                if (hierarchy != null) {
                    for (let i = 0; i < hierarchy.length; i++) {
                        if (hierarchy[i].id === pasteOperation.Fso!.id) {
                            return false;
                        }
                    }
                }
                return true;
            }
        }
        private async Fso_Paste() {
            if (this.Can_Paste) {

                //let pasteOperation = this.$store.state.explorer.PasteOperationDetails as ext.PasteOperation;
                //let name = pasteOperation.Fso!.name;
                //if (this.Fso.folderContent != null)
                //    for (let i = 0; i < this.Fso.folderContent.length; i++) {
                //        if (name == this.Fso.folderContent[i].name) {
                //            alert(name + " already present in current folder.");
                //            return;
                //        }
                //    }

                this.Working = true;
                try {
                    await this.$store.dispatch('explorer/PasteToTargetItem');

                    this.CloseContextMenu();
                }
                catch (err) {
                    this.$store.dispatch('ShowToastError', err);
                }
                this.Working = false;
            }
        }

        private get Can_Export(): boolean {
            const isContents = function (type: api.FileSystemType) {
                return type == api.FileSystemType.Generic
                    || type == api.FileSystemType.ReportBook
                    || type == api.FileSystemType.DocxReport
                    || type == api.FileSystemType.PptxReport
                    || type == api.FileSystemType.PowerBI
                    || type == api.FileSystemType.CalcScript
                    || type == api.FileSystemType.XL3
                    || type == api.FileSystemType.Process
                    || type == api.FileSystemType.InteractiveProcess
                    || type == api.FileSystemType.Form;
            };
            if (isContents(this.Fso.objectType) == true) {
                return this.Can_AccessObjectContents;
            }
            else {
                switch (this.Fso.objectType) {
                    case api.FileSystemType.Folder:
                        return this.Fso.folderContent != null && this.Fso.folderContent.length > 0;
                    default:
                        return false;
                }
            }
        }

        private async Fso_Export() {
            if (this.Can_Export) {
                this.Working = true;
                const authClient = apiBase.AuthClient.getInstance();
                await authClient.ensureToken();
                const client = new api.FileSystemClient(authClient);

                if (this.Fso.path) {
                    const response = await client.export(this.FullPath);
                    if (response) {
                        const blob = new Blob([response.data], { type: 'application/zip' });
                        const link = document.createElement('a');
                        link.href = URL.createObjectURL(blob);
                        // if the header content-disposition is missing from the response
                        // (for some reason) then a new name will be given
                        // otherwise, the file name should come from FileSystemController.Export()
                        link.download = response.fileName ? response.fileName :
                            `${this.Fso.name == '/' ? 'File_System' : this.Fso.name}.zip`;
                        link.click();
                        URL.revokeObjectURL(link.href);
                    }
                }
                this.CloseContextMenu();
                this.Working = false;
            }
        }

        private get Can_Import(): boolean {
            let result = this.Can_NewFolderObjects;
            if (this.Fso.objectType == api.FileSystemType.Process ||
                this.Fso.objectType == api.FileSystemType.InteractiveProcess) {
                result = result && this.UserCanEditWorkflow;
            }
            return result;
        }
        private async Fso_Import() {
            if (this.Can_Import) {
                this.CloseContextMenu();

                this.$store.commit('explorer/SelectFso', this.Fso);

                const hiddenInput = document.createElement('input');
                hiddenInput.type = 'file';
                hiddenInput.accept = '.zip';
                hiddenInput.addEventListener('change', async (event: Event) => {
                    const file = hiddenInput.files![0];
                    try {
                        this.Working = true;
                        await this.$store.dispatch('explorer/ImportIntoSelectedItem', file);
                        const fsoId = this.Fso.id;
                        await this.$store.dispatch('explorer/LoadFileSystem');
                        const updatedFso = this.$store.getters['explorer/getFsoById'](fsoId);
                        if (updatedFso) {
                            this.$store.commit('explorer/SelectFso', updatedFso);
                        }
                        await this.$store.dispatch('ShowToast', new api.ToastNotification({
                            message: this.Localizer.Localize('FileSystemObject_Import_has_completed'),
                            type: api.ToastType.Info
                        }));
                    }
                    catch (err) {
                        await this.$store.dispatch('ShowToastError', err);
                    }
                    finally {
                        this.Working = false;
                    }
                }, false);
                hiddenInput.click();
            }
        }

        get User(): api.ExtendedUserData {
            return this.$store.state.security.UserData;
        }
        private get Can_Security(): boolean {
            return this.User.isSuperUser ||
                (this.ModuleSecurityAccess != null &&
                    ModuleAuthHelper.ModuleIdInAccess(this.ModuleSecurityAccess,api.ModuleAccess.SecurityFileSystemSecurityAssign));
        }
        private async Fso_Security() {
            if (this.Can_Security) {
                this.$store.commit('explorer/SetFsoToEditSecurity', this.Fso);
                this.CloseContextMenu();
            }
        }

        private get Can_FixProcessIds(): boolean {
            return this.User.isSuperUser && this.Fso.objectType == api.FileSystemType.InteractiveProcess;
        }
        private async FixProcessIds() {
            const authClient = apiBase.AuthClient.getInstance();
            await authClient.ensureToken();
            const client = new api.ProcessClient(authClient);

            if (this.Fso.path) {
                const response = await client.fixInteractive(this.FullPath);
            }
        }

        private FormatType(val: api.FileSystemType): string {
            return this.Localizer.FormatFileType(val);
        }

        private FormatDate(date: Date | null | undefined): string {
            if (date == null || date == undefined) {
                return '';
            }
            const currentDate = new Date();
            const localDate = new Date(date.getTime() - currentDate.getTimezoneOffset() * 60000);

            const year = localDate.getFullYear();
            let month = `${localDate.getMonth() + 1}`,
                day = `${localDate.getDate()}`,
                hour = `${localDate.getHours()}`,
                min = `${localDate.getMinutes()}`,
                sec = `${localDate.getSeconds()}`;

            if (month.length < 2) {
                month = `0${month}`;
            }
            if (day.length < 2) {
                day = `0${day}`;
            }

            if (hour.length < 2) {
                hour = `0${hour}`;
            }
            if (min.length < 2) {
                min = `0${min}`;
            }
            if (sec.length < 2) {
                sec = `0${sec}`;
            }

            return `${[year, month, day].join('-')} ${[hour, min, sec].join(':')}`;
        }

        private get IsCurrentSearchResult(): boolean {
            return this.HighlightSearchResults &&
                this.$store.state.explorer.CurrentSearchResult != null &&
                this.$store.state.explorer.CurrentSearchResult.fullPath == this.Fso.fullPath;
        }

        @Watch('IsCurrentSearchResult') OnIsCurrentSearchResultChanged(val: boolean) {
            if (val) {
                const el = this.$refs.fso as HTMLDivElement;
                el.scrollIntoView({ block: 'nearest' });
            }
        }

        private get IsSearchResult() {
            const st = this.$store.state.explorer.SearchText;
            if (st.length == 0) {
                return false;
            }
            return this.HighlightSearchResults && this.Fso.name!.toLowerCase().indexOf(st) >= 0;
        }

        private FormatLastModifiedBy(lastModifiedBy: string | null | undefined): string {
            if (lastModifiedBy == null || lastModifiedBy == undefined || lastModifiedBy.trim() === '') {
                return '';
            }
            return lastModifiedBy;
        }
    }

</script>

<template>
    <div v-if="UserCanView"
         ref="fso"
         class="fso"
         :class="{disabled: IsThisFolderDisabled, 'is-global': IsGlobal}">
        <div :class="{
        selected:IsSelected,
        cutcopySelected: IsCutCopySelected,
        hover: true ,
        detailed:HideExpander==true,
        detailed_v2: HideExpander==true,
      }"
             class="fso-main"
             @click.stop="Fso_Click"
             @dblclick="Fso_Dblclick()"
             @contextmenu.prevent.stop="Fso_ContextMenu">
            <div v-if="Fso.objectType==FileSystemType.Folder && HideExpander!=true"
                 class="expander"
                 :class="{expanded:IsExpanded, collapsed:!IsExpanded, fschidden:!HasChildren}"
                 @click.stop="Expander_Click" />
            <div>
                <FileSystemObjectIcon :Fso="Fso" />

                <img v-if="IsGlobal && Fso.objectType==FileSystemType.Folder"
                     style="position:relative; top: 0.06rem; left:-17px;"
                     src="../../../../public/icons/explorer/global.svg"
                     class="global-icon-color">
                <div v-if="IsGlobal && Fso.objectType!=FileSystemType.Folder"
                     style="position: relative; top: 19px; left: -6px; height: 8px;
                            width: 8px; border-radius: 100%;
                            background: var(--background-striped); display:inline-block;">
                    <img src="../../../../public/icons/explorer/global.svg"
                         style="position:relative; top:-11.5px; right: 1px"
                         :class="{'accent-svg':!IsSelected}">
                </div>
            </div>

            <span class="name">
                <span :class="{
            nameInner:true,
            searchResult: IsSearchResult,
            currentSearchResult: IsCurrentSearchResult
          }" :title="Fso.name">
                    {{ Fso.name }}
                </span>

            </span>
            <span class="alm-badge" v-if="isAlm && !Secondary && almBadgeCount(Fso) !== 0">{{almBadgeCount(Fso)}}</span>

            <span v-if="HideExpander==true" class="name">{{ FormatType(Fso.objectType) }}</span>
            <span v-if="HideExpander==true" class="name">{{ FormatDate(Fso.lastModified) }}</span>
            <span v-if="HideExpander==true" class="name">{{ FormatLastModifiedBy(Fso.lastModifiedBy) }}</span>

            <img v-if="(Fso.objectType!=FileSystemType.Folder || HideExpander) && IsFavorite && !isAlm"
                 class="icon"
                 src="../../../../public/icons/explorer/star.svg"
                 @click.stop.prevent="ToggleFavorite">
            <img v-if="(Fso.objectType!=FileSystemType.Folder || HideExpander) && !IsFavorite && !isAlm"
                 class="icon theme-svg"
                 src="../../../../public/icons/explorer/star-empty.svg"
                 @click.stop.prevent="ToggleFavorite">

            <img v-if="!IsThisFolderDisabled && Fso.id!=0.1 && !isAlm"
                 class="icon theme-svg"
                 src="../../../../public/icons/explorer/context_menu/ellipses.svg"
                 @click.prevent.stop="Fso_ContextMenu">
        </div>

        <transition name="fso-expanded">
            <div v-if="IsExpanded && HideExpander!=true" class="children">
                <transition-group name="fso-expanded" tag="span">
                    <FileSystemObject v-for="child in FolderChildren"
                                      :isAlm="isAlm"
                                      :key="child.id"
                                      :Fso="child"
                                      :CanUserExpand="true"
                                      :CanUserSelect="CanUserSelect"
                                      :ChangeUrlOnSelect="ChangeUrlOnSelect"
                                      :CanUserOpenWithDoubleClick="CanUserOpenWithDoubleClick"
                                      :IsParentFolderDisabled="IsThisFolderDisabled"
                                      :Secondary="Secondary" />
                </transition-group>
            </div>
        </transition>

        <ContextMenu v-if="CanShowContextMenu && ShowContextMenu"
                     :TriggerEvent="ContextMenuEvent"
                     @close="ContextMenuEvent=null">
            <template #buttons>
                <div v-if="Fso.objectType!=FileSystemType.Folder &&
            Fso.objectType!=FileSystemType.ReportBook &&
            Fso.objectType!=FileSystemType.CalcScript"
                     class="context_menu_btn"
                     :class="{disabled: !Can_Open}"
                     @click="Fso_Open()">
                    <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/open.svg">
                    &nbsp;{{ Localizer.Localize("FileSystemObject_Open") }}
                </div>
                <div v-if="Fso.objectType==FileSystemType.XL3"
                     class="context_menu_btn"
                     :class="{disabled: !Can_OpenInExcel}"
                     @click="Fso_OpenInExcel()">
                    <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/open.svg">
                    &nbsp;{{ Localizer.Localize("FileSystemObject_Open_in_Excel") }}
                </div>
                <div v-if="Fso.objectType==FileSystemType.XL3 || Fso.objectType==FileSystemType.PowerBI"
                     class="context_menu_btn"
                     :class="{disabled: !Can_OpenInPopup}"
                     @click="Fso_OpenInPopup()">
                    <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/open.svg">
                    &nbsp;Open in Popup
                </div>
                <div v-if="Fso.objectType==FileSystemType.XL3"
                     class="context_menu_btn"
                     :class="{disabled: !Can_OpenInTab}"
                     @click="Fso_OpenNewTab()">
                    <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/open.svg">
                    &nbsp;{{ Localizer.Localize("FileSystemObject_Open_Newtab") }}
                </div>
                <div v-if="Fso.objectType===FileSystemType.ReportBook || Fso.objectType===FileSystemType.CalcScript"
                     class="context_menu_btn"
                     :class="{disabled: !Can_Run}"
                     @click="Fso_Run()">
                    <img class="theme-svg" src="../../../../public/icons/workflow/run.svg">
                    &nbsp;{{ Localizer.Localize("FileSystemObject_Run") }}
                </div>
                <ContextMenuButton v-if="Fso.objectType===FileSystemType.Folder"
                                   :title="Localizer.Localize('FileSystemObject_New')">
                    <template #img>
                        <img class="theme-svg" src="../../../../public/icons/data/add.svg">
                    </template>
                    <template #submenu>
                        <div class="context_menu_btn"
                             :class="{disabled: !Can_NewFolder}"
                             @click="Fso_NewFolder()">
                            <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/new_folder.svg">
                            &nbsp;{{ Localizer.Localize('FileSystemObject_Folder') }}
                        </div>
                        <div class="context_menu_btn"
                             :class="{disabled: !Can_NewProcess}"
                             @click="Fso_NewProcess()">
                            <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/new_process.svg">
                            &nbsp;{{ Localizer.Localize('FileSystemObject_Process') }}
                        </div>
                        <div class="context_menu_btn"
                             :class="{disabled: !Can_NewReportBook}"
                             @click="Fso_NewReportBook()">
                            <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/new_reportbook.svg">
                            &nbsp;{{ Localizer.Localize('FileSystemObject_Report_Book') }}
                        </div>
                        <div class="context_menu_btn"
                             :class="{disabled: !Can_NewPowerBI}"
                             @click="Fso_NewPowerBI()">
                            <img class="theme-svg" src="../../../../public/icons/explorer/power_bi.svg">
                            &nbsp;{{ Localizer.Localize('FileSystemObject_Power_BI_Report') }}
                        </div>
                        <div class="context_menu_btn"
                             :class="{disabled: !Can_NewOfficeReport}"
                             @click="Fso_NewDocxReport()">
                            <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/new_word.svg">
                            &nbsp;{{ Localizer.Localize('FileSystemObject_Word_Report') }}
                        </div>
                        <div class="context_menu_btn"
                             :class="{disabled: !Can_NewOfficeReport}"
                             @click="Fso_NewPptxReport()">
                            <img class="theme-svg"
                                 src="../../../../public/icons/explorer/context_menu/new_ppt.svg">
                            &nbsp;{{ Localizer.Localize('FileSystemObject_Powerpoint_Report') }}
                        </div>
                        <div class="context_menu_btn"
                             :class="{disabled: !Can_NewXL3}"
                             @click="Fso_NewXL3()">
                            <img class="theme-svg"
                                 src="../../../../public/icons/explorer/context_menu/new_xl3.svg">
                            &nbsp;{{ Localizer.Localize('FileSystemObject_XL3_Report') }}
                        </div>
                        <div class="context_menu_btn"
                             :class="{disabled: !Can_NewCalcScript}"
                             @click="Fso_NewCalcScript()">
                            <img class="theme-svg"
                                 src="../../../../public/icons/explorer/context_menu/new_calcscript.svg">
                            &nbsp;{{ Localizer.Localize('FileSystemObject_CalcScript') }}
                        </div>
                        <div class="context_menu_btn" :class="{disabled: !Can_NewGeneric}" @click="Fso_NewGeneric()">
                            <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/new_generic.svg">
                            &nbsp;{{ Localizer.Localize('FileSystemObject_Generic_File') }}
                        </div>
                    </template>
                </ContextMenuButton>
                <hr>
                <div class="context_menu_btn" :class="{disabled: !Can_Cut}" @click="Fso_Cut()">
                    <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/cut.svg">
                    &nbsp;{{ Localizer.Localize("FileSystemObject_Cut") }}
                </div>
                <div class="context_menu_btn" :class="{disabled: !Can_Copy}" @click="Fso_Copy()">
                    <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/copy.svg">
                    &nbsp;{{ Localizer.Localize("FileSystemObject_Copy") }}
                </div>
                <div v-if="Fso.objectType===FileSystemType.Folder"
                     class="context_menu_btn"
                     :class="{disabled: !Can_Paste}"
                     @click="Fso_Paste()">
                    <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/paste.svg">
                    &nbsp;{{ Localizer.Localize("FileSystemObject_Paste") }}
                </div>
                <hr>
                <div v-if="Fso.objectType===FileSystemType.Form
            || Fso.objectType===FileSystemType.ReportBook
            || Fso.objectType===FileSystemType.DocxReport
            || Fso.objectType===FileSystemType.PptxReport"
                     class="context_menu_btn"
                     :class="{disabled: !Can_Edit}"
                     @click="Fso_Edit()">
                    <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/edit.svg">
                    &nbsp;{{ Localizer.Localize("FileSystemObject_Edit") }}
                </div>
                <div class="context_menu_btn" :class="{disabled: !Can_Rename}" @click="Fso_Rename()">
                    <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/rename.svg">
                    &nbsp;{{ Localizer.Localize("FileSystemObject_Rename") }}
                </div>
                <div class="context_menu_btn" :class="{disabled: !Can_Delete}" @click="OpenConfirmDelete()">
                    <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/delete.svg">
                    &nbsp;{{ Localizer.Localize("FileSystemObject_Delete") }}
                </div>
                <hr>
                <div class="context_menu_btn" :class="{disabled: !Can_Export}" @click="Fso_Export()">
                    <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/export.svg">
                    &nbsp;{{ Localizer.Localize("FileSystemObject_Export") }}
                </div>
                <ContextMenuButton v-if="Fso.objectType==FileSystemType.XL3"
                                   :title="Localizer.Localize('Export As')"
                                   :class="{disabled: !Can_SaveAs}">
                    <template #img>
                        <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/export_as.svg">
                    </template>
                    <template #submenu>
                        <div class="context_menu_btn" @click="Fso_SaveAs('pdf')">
                            <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/pdf.svg">
                            &nbsp;PDF
                        </div>
                        <div class="context_menu_btn" @click="Fso_SaveAs('excel')">
                            <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/excel.svg">
                            &nbsp;Excel
                        </div>
                        <div class="context_menu_btn" @click="Fso_SaveAs('powerpoint')">
                            <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/ppt.svg">
                            &nbsp;Powerpoint
                        </div>
                    </template>
                </ContextMenuButton>
                <div v-if="Fso.objectType===FileSystemType.Folder"
                     class="context_menu_btn"
                     :class="{disabled: !Can_Import}"
                     @click="Fso_Import()">
                    <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/import.svg">
                    &nbsp;{{ Localizer.Localize("FileSystemObject_Import") }}
                </div>
                <hr>
                <div v-if="Fso.objectType===FileSystemType.Folder"
                     class="context_menu_btn"
                     :class="{disabled: !Can_Security}"
                     @click="Fso_Security()">
                    <img class="theme-svg" src="../../../../public/icons/users/roles.svg">
                    &nbsp;{{ Localizer.Localize("FileSystemObject_Security") }}
                </div>
                <div v-if="Can_FixProcessIds" class="context_menu_btn" @click="FixProcessIds()">
                    <img class="theme-svg" src="../../../../public/icons/users/roles.svg">
                    &nbsp;Fix Process Ids
                </div>
                <div v-if="Fso.name==='/'"
                     class="context_menu_btn"
                     @click="Refresh()">
                    <img class="theme-svg" src="../../../../public/icons/explorer/context_menu/refresh.svg">
                    &nbsp;{{ Localizer.Localize("Refresh") }}
                </div>
            </template>
        </ContextMenu>

        <Confirm v-if="ShowConfirmDelete" @close="CloseConfirmDelete">
            <template #icon>
                <div style="float:left;">
                    <img class="icon" src="../../../../public/icons/info.png" style="width: 50px; height:50px;">
                </div>
            </template>
            <template #body>
                <div style="padding-top:15px;">
                    {{ Localizer.Localize("File_Confirm_Delete") }}
                </div>
            </template>
            <template #footer>
                <CommonButton
                    :type="ButtonType.Default" 
                    :text="Localizer.Localize('Cancel_PascalCase')" 
                    @click="CloseConfirmDelete"/>
                <CommonButton
                    :type="ButtonType.Primary" 
                    :text="Localizer.Localize('Delete_PascalCase')"
                    @click="Fso_Delete(); CloseConfirmDelete()"/>
            </template>
        </Confirm>

    </div>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
    .fso {
        padding-bottom: 0px;
        cursor: pointer;
        user-select: none;
    }

        .fso.disabled {
            cursor: default;
            opacity: 0.5;
        }

    .fso-main {
        padding-top: 5px;
        padding-bottom: 5px;
        line-height: 28px;
        height: 36px;
        position: relative;
        white-space: nowrap;
        display: grid;
        grid-template-columns: 24px 24px 1fr 40px;
        grid-column-gap: 6px;
        justify-items: stretch;
        align-items: center;
        overflow: hidden;
        text-overflow: ellipsis;
    }

        .fso-main > * {
            min-width: 0px;
        }

        .fso-main.detailed{
            grid-template-columns: 24px 2fr 60px 150px 24px 24px;
            padding-left: 6px;
        }

        .fso-main.detailed_v2 {
            grid-template-columns: 24px 2fr calc(var(--col-type, 155px) - 10px) var(--col-changed, 175px) calc(var(--col-lastModifiedBy, 160px) - 10px) 25px 24px;
            grid-column-gap: 6px;
            padding-left: 6px;
        }

    .name {
        position: relative;
        overflow: hidden;
        text-overflow: ellipsis;
        top: 2px;
    }

    .cutcopySelected {
        background-color: #2e75ff0c;
        border: 1px dotted #2e75ff;
    }

    .selected {
        background-color: var(--accent-color2) !important;
    }

    .hover:hover {
        background-color: var(--row-hover) !important;
    }

    .fschidden {
        visibility: hidden;
    }

    .icon {
        width: 24px;
        height: 24px;
    }

    .children {
        padding-left: 20px;
    }

    .fso-expanded-enter-active, .fso-expanded-leave-active {
        transition: all .3s ease;
    }

    .fso-expanded-enter-from, .fso-expanded-leave-to {
        transform: translateX(-10px);
        opacity: 0;
    }

    .searchResult {
        background-color: #cce4fc;
    }

    .currentSearchResult {
        background-color: #ffff55 !important;
    }

    .global-icon-color {
        filter: brightness(0) invert(1);
    }

    .alm-badge {
        display: inline-flex;
        height: 15px;
        gap: 2px;
        border-radius: 4px;
        padding: 4px;
        background-color: var(--background-primary);
        border: solid 1px var(--accent-color5);
        font-weight: 600;
        font-size: 9px;
        line-height: 7px;
        color: var(--accent-color5);
        margin-left: 4px;
        margin-top: 2px;
        margin-right: 12px;
    }
</style>
